import { useCallback, useEffect, useState } from 'react';
import { safeWindowOpen } from '~/helpers/safeUrlOpen.ts';
import { withBlobContentType } from '~/helpers/utils.ts';
import { GuaranteesFetchStatus } from '~/store/guarantees/types.ts';

type TSetModalAlert = (value: { text: string }) => void;

export const useGuaranteesPdfPreview = ({
  contentType,
  documentBlob,
  missingPdfText,
  setModalAlert,
  status,
}: {
  contentType?: string;
  documentBlob?: Blob | null;
  missingPdfText: string;
  setModalAlert: TSetModalAlert;
  status?: GuaranteesFetchStatus;
}) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const isDocumentLoading = status === GuaranteesFetchStatus.Loading;

  const handleOpenPdf = useCallback(() => {
    if (!pdfUrl) {
      setModalAlert({ text: missingPdfText });

      return;
    }

    safeWindowOpen(pdfUrl, { base: window.location.origin });
  }, [missingPdfText, pdfUrl, setModalAlert]);

  useEffect(() => {
    if (!(documentBlob instanceof Blob)) {
      setPdfUrl('');

      return;
    }

    const previewBlob = withBlobContentType(documentBlob, contentType);
    const nextPdfUrl = URL.createObjectURL(previewBlob);

    setPdfUrl(nextPdfUrl);

    return () => {
      URL.revokeObjectURL(nextPdfUrl);
    };
  }, [contentType, documentBlob]);

  useEffect(() => {
    if (status === GuaranteesFetchStatus.Success && !(documentBlob instanceof Blob)) {
      setModalAlert({ text: missingPdfText });
    }
  }, [documentBlob, missingPdfText, setModalAlert, status]);

  return {
    handleOpenPdf,
    isDocumentLoading,
    pdfUrl,
  };
};
